import React from "react";
import { Button, Modal } from "react-bootstrap";

const PERIOD_TYPE_STYLES = {
  assembly: { bg: "#f5a623", color: "#ffffff" },
  break: { bg: "#16a34a", color: "#ffffff" },
  period: { bg: "#3b82f6", color: "#ffffff" },
  default: { bg: "#6b7280", color: "#ffffff" },
};

const escapeHtml = (value = "") =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const getPeriodStyle = (periodType) =>
  PERIOD_TYPE_STYLES[periodType] || PERIOD_TYPE_STYLES.default;

const getWorkingDaysList = (workingDays = 6) =>
  [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ].slice(0, workingDays);

const getTimetablePrintStyles = () => `
  * { box-sizing: border-box; }
  body {
    margin: 0;
    font-family: Arial, sans-serif;
    color: #111827;
    background: #ffffff;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .print-sheet {
    width: 100%;
    min-height: 48vh;
    margin: 0;
    padding: 10px;
    page-break-inside: avoid;
    display: block;
  }
  .print-sheet:nth-child(2n) {
    page-break-after: always;
  }
  .sheet-title {
    margin: 0 0 12px;
    text-align: center;
    font-size: 16px;
    font-weight: 700;
    color: #1f3b73;
  }
  .timetable-table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
  }
  .timetable-table th,
  .timetable-table td {
    border: 1px solid #cfd4dc;
    padding: 6px 4px;
    text-align: center;
    vertical-align: middle;
    font-size: 10px;
  }
  .timetable-table thead th {
    background: #f8fafc;
    font-weight: 700;
  }
  .day-period-heading,
  .day-cell {
    width: 11%;
    background: #f3f4f6;
    font-weight: 700;
    text-align: left;
  }
  .period-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
  }
  .period-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 18px;
    padding: 0 8px;
    border-radius: 999px;
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    white-space: nowrap;
  }
  .period-time {
    font-size: 8px;
    color: #4b5563;
    white-space: nowrap;
  }
  .slot-cell-muted {
    background: #f8fafc;
  }
  .slot-subject {
    font-weight: 700;
    line-height: 1.25;
  }
  .slot-teacher {
    margin-top: 2px;
    font-size: 8px;
    color: #4b5563;
    line-height: 1.2;
  }
  @page {
    size: A4 portrait;
    margin: 16mm 12mm;
  }
`;

export const getTimetablePrintTitle = (sectionTimetable = {}) => {
  const className = sectionTimetable?.class_name?.trim?.() || "";
  const sectionName = sectionTimetable?.section_name?.trim?.() || "";

  // If section already contains class, don't repeat
  if (sectionName.toLowerCase().includes(className.toLowerCase())) {
    return `${sectionName} Timetable`;
  }

  return `${className} ${sectionName} Timetable`.trim();
};

export const buildTimetablePrintTable = ({
  title,
  periods = [],
  timetableData = [],
  workingDays = 6,
}) => {
  const days = getWorkingDaysList(workingDays);

  const headerCells = periods
    .map((period) => {
      const style = getPeriodStyle(period.period_type);

      return `
        <th>
          <div class="period-header">
            <span
              class="period-badge"
              style="background:${style.bg};color:${style.color};"
            >
              ${escapeHtml(period.period_name || "")}
            </span>
            <div class="period-time">
              ${escapeHtml(period.start_time?.slice?.(0, 5) || "")}
              -
              ${escapeHtml(period.end_time?.slice?.(0, 5) || "")}
            </div>
          </div>
        </th>
      `;
    })
    .join("");

  const rows = days
    .map((day) => {
      const cells = periods
        .map((period) => {
          const slot = timetableData.find(
            (item) =>
              item.day === day && Number(item.period_id) === Number(period.period_id)
          );

          if (period.period_type !== "period") {
            return `
              <td class="slot-cell slot-cell-muted">
                <div class="slot-subject">${escapeHtml(period.period_name || "")}</div>
              </td>
            `;
          }

          return `
            <td class="slot-cell">
              <div class="slot-subject">
                ${escapeHtml(slot?.subject_name || "Free Period")}
              </div>
              <div class="slot-teacher">
                ${escapeHtml(slot?.teacher_name || "")}
              </div>
            </td>
          `;
        })
        .join("");

      return `
        <tr>
          <td class="day-cell">${escapeHtml(day)}</td>
          ${cells}
        </tr>
      `;
    })
    .join("");

  return `
    <section class="print-sheet">
      <h2 class="sheet-title">${escapeHtml(title)}</h2>
      <table class="timetable-table">
        <thead>
          <tr>
            <th class="day-period-heading">Day / Period</th>
            ${headerCells}
          </tr>
        </thead>
        <tbody>
          ${rows}
        </tbody>
      </table>
    </section>
  `;
};

export const printTimetableDocuments = ({ documents = [] }) => {
  if (!documents.length) return;

  const content = documents
    .map((document) => buildTimetablePrintTable(document))
    .join("");

  const printFrame = document.createElement("iframe");
  printFrame.style.position = "fixed";
  printFrame.style.right = "0";
  printFrame.style.bottom = "0";
  printFrame.style.width = "0";
  printFrame.style.height = "0";
  printFrame.style.border = "0";
  printFrame.setAttribute("aria-hidden", "true");

  document.body.appendChild(printFrame);

  const frameWindow = printFrame.contentWindow;
  if (!frameWindow) {
    document.body.removeChild(printFrame);
    return;
  }

  frameWindow.document.open();
  frameWindow.document.write(`
    <html>
      <head>
        <title>Timetable</title>
        <style>
          * { box-sizing: border-box; }
          body {
            margin: 0;
            font-family: Arial, sans-serif;
            color: #111827;
            background: #ffffff;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .print-sheet {
            width: 100%;
            height: 48vh;              /* ✅ key fix: only 2 fit per page */
            margin: 0;
            padding: 10px;
            page-break-inside: avoid;
            display: block;
          }
          .print-sheet:nth-child(2n) {
            page-break-after: always;   /* ✅ every 2 → new page */
        }
          .sheet-title {
            margin: 0 0 12px;
            text-align: center;
            font-size: 16px;
            font-weight: 700;
            color: #1f3b73;
          }
          .timetable-table {
            width: 100%;
            border-collapse: collapse;
            table-layout: fixed;
          }
          .timetable-table th,
          .timetable-table td {
            border: 1px solid #cfd4dc;
            padding: 6px 4px;
            text-align: center;
            vertical-align: middle;
            font-size: 10px;
          }
          .timetable-table thead th {
            background: #f8fafc;
            font-weight: 700;
          }
          .day-period-heading,
          .day-cell {
            width: 11%;
            background: #f3f4f6;
            font-weight: 700;
            text-align: left;
          }
          .period-header {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 4px;
          }
          .period-badge {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            min-height: 18px;
            padding: 0 8px;
            border-radius: 999px;
            font-size: 9px;
            font-weight: 700;
            text-transform: uppercase;
            white-space: nowrap;
          }
          .period-time {
            font-size: 8px;
            color: #4b5563;
            white-space: nowrap;
          }
          .slot-cell-muted {
            background: #f8fafc;
          }
          .slot-subject {
            font-weight: 700;
            line-height: 1.25;
          }
          .slot-teacher {
            margin-top: 2px;
            font-size: 8px;
            color: #4b5563;
            line-height: 1.2;
          }
          @page {
            size: A4 portrait;
            margin: 16mm 12mm;
          }
        </style>
      </head>
      <body>${content}</body>
    </html>
  `);
  frameWindow.document.close();
  frameWindow.focus();

  setTimeout(() => {
    frameWindow.print();
  }, 300);
};

export const TimetablePrintPreviewModal = ({
  show,
  onHide,
  documents = [],
}) => {
  if (!show) return null;

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>Timetable Print Preview</Modal.Title>
      </Modal.Header>
      <Modal.Body
        style={{ maxHeight: "75vh", overflowY: "auto", background: "#f3f4f6" }}
      >
        <style>{getTimetablePrintStyles()}</style>
        <div className="bg-white">
          {documents.map((document, index) => (
            <TimetablePrintSheet
              key={`${document.title}-${index}`}
              {...document}
            />
          ))}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
        <Button
          variant="primary"
          onClick={() => printTimetableDocuments({ documents })}
        >
          Print
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

function TimetablePrintSheet({
  title,
  periods = [],
  timetableData = [],
  workingDays = 6,
}) {
  const days = getWorkingDaysList(workingDays);

  return (
    <section className="print-sheet">
      <h2 className="sheet-title">{title}</h2>
      <table className="timetable-table">
        <thead>
          <tr>
            <th className="day-period-heading">Day / Period</th>
            {periods.map((period) => {
              const style = getPeriodStyle(period.period_type);

              return (
                <th key={period.period_id}>
                  <div className="period-header">
                    <span
                      className="period-badge"
                      style={{ background: style.bg, color: style.color }}
                    >
                      {period.period_name || ""}
                    </span>
                    <div className="period-time">
                      {period.start_time?.slice?.(0, 5) || ""} -{" "}
                      {period.end_time?.slice?.(0, 5) || ""}
                    </div>
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {days.map((day) => (
            <tr key={day}>
              <td className="day-cell">{day}</td>
              {periods.map((period) => {
                const slot = timetableData.find(
                  (item) =>
                    item.day === day &&
                    Number(item.period_id) === Number(period.period_id)
                );

                if (period.period_type !== "period") {
                  return (
                    <td
                      key={`${day}-${period.period_id}`}
                      className="slot-cell slot-cell-muted"
                    >
                      <div className="slot-subject">
                        {period.period_name || ""}
                      </div>
                    </td>
                  );
                }

                return (
                  <td key={`${day}-${period.period_id}`} className="slot-cell">
                    <div className="slot-subject">
                      {slot?.subject_name || "Free Period"}
                    </div>
                    <div className="slot-teacher">
                      {slot?.teacher_name || ""}
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function PrintDataTable({ columns, data, tableRef, deleteAccessor }) {
  const filterdColumns = columns.filter(
    (column) => column.accessor !== deleteAccessor
  );

  return (
    <div className="table-responsive table-container">
      <table className="table" id="contentToPrint" ref={tableRef}>
        <thead>
          <tr>
            {filterdColumns.map((col) => (
              <th key={col.accessor}>{col.Header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, index) => (
            <tr key={row.id || index}>
              {filterdColumns.map((col) => (
                <td key={col.accessor}>
                  {col.accessor === "sno" ? index + 1 : row[col.accessor]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default PrintDataTable;